import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Form from "react-bootstrap/Form";
import OtpInput from "react-otp-input";
import { toast } from "react-toastify";

import colorSchemes from "../../../constants/colorSchemes";
import routes from "../../../constants/routes";
import Loader from "../../../common/Loader";
import getUserProfileAPI from "../../../api/getUserProfileAPI";
import logoutAPI from "../../../api/logoutAPI";
import {
  changeEmailAPI,
  resendChangeEmailCodeAPI,
  sendEmailCodeAPI,
  verifyChangeEmailAPI,
  verifyEmailAPI,
} from "../../../api/emailVerificationAPI";
import EmailCodeBox, { errorText } from "../../commonComponents/EmailCodeBox";
import { markEmailConfirmed } from "../../../common/emailStatus";
import {
  clearSessionAuth,
  removeToken,
  removeUserData,
} from "../../../common";

const buttonSx = {
  backgroundColor: colorSchemes.primaryColor,
  color: "#fff",
  "&.MuiButtonBase-root:hover": { bgcolor: "#2D55EB !important" },
};
const textSx = { color: "#7E8184", fontSize: "14px", margin: "1em 0" };
const linkSx = { fontSize: "14px", margin: "0.75em 0" };

// "Confirm your email" (the only page until the email is confirmed), "My
// email is wrong — change it", "Add your email" (the email went to the person
// who confirmed it) and Change email from the profile (?mode=change).
export default function ConfirmEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  // confirm | confirmCode | change | changeCode
  const [step, setStep] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [currentCode, setCurrentCode] = useState("");
  const [currentCodeSent, setCurrentCodeSent] = useState(false);
  const [waitSeconds, setWaitSeconds] = useState(undefined);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    getUserProfileAPI()
      .then(({ data }) => {
        setProfile(data);
        const wantsChange = searchParams.get("mode") === "change";
        setStep(!data.email || wantsChange ? "change" : data.emailVerified ? null : "confirm");
        if (data.email && data.emailVerified && !wantsChange) {
          markEmailConfirmed(data.email);
          navigate(routes.home);
        }
      })
      .catch(() => navigate(routes.signin));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = (event) => {
    event?.preventDefault();
    logoutAPI().catch(() => null);
    removeToken();
    removeUserData();
    clearSessionAuth();
    window.location.hash = routes.signin;
  };

  const finished = (data) => {
    markEmailConfirmed(data.email);
    toast(data.message);
    navigate(searchParams.get("mode") === "change" ? routes.profile : routes.home);
  };

  const run = async (action) => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      await action();
    } catch (error) {
      toast(errorText(error));
      const wait = error?.response?.data?.waitSeconds;
      // asked again too soon: the code already sent still works
      if (wait && step === "confirm") {
        setWaitSeconds(wait);
        setStep("confirmCode");
      }
      if (wait && step === "change" && error.response.data.email) {
        setWaitSeconds(wait);
        setStep("changeCode");
      }
    }
    setIsBusy(false);
  };

  const sendConfirmCode = () =>
    run(async () => {
      await sendEmailCodeAPI();
      setWaitSeconds(undefined);
      setStep("confirmCode");
    });

  const sendCurrentCode = () =>
    run(async () => {
      await sendEmailCodeAPI();
      setCurrentCodeSent(true);
      toast(`We have sent a 6-digit code to ${profile.email}.`);
    });

  const needsCurrentCode = profile && !profile.hasPassword && profile.email && profile.emailVerified;

  const startChange = (event) => {
    event.preventDefault();
    run(async () => {
      const email = newEmail.trim().toLowerCase();
      await changeEmailAPI(
        needsCurrentCode ? { newEmail: email, currentCode } : { newEmail: email, password }
      );
      setNewEmail(email);
      setWaitSeconds(undefined);
      setStep("changeCode");
    });
  };

  if (!profile || !step) return <Loader isLoading={true} />;

  const isAdding = !profile.email;
  const title = isAdding
    ? "Add your email"
    : step.startsWith("confirm")
      ? "Confirm your email"
      : "Change your email";

  return (
    <div className="container" style={{ display: "flex", justifyContent: "center" }}>
      <Card sx={{ maxWidth: 520, width: "100%", marginY: 5, padding: "1.5em 2em" }}>
        <Typography
          variant="h5"
          sx={{ textAlign: "center", fontWeight: "600", color: colorSchemes.primaryColor }}
        >
          {title}
        </Typography>

        {step === "confirm" && (
          <>
            <Typography sx={textSx}>
              To keep using v3Bid, please confirm <strong>{profile.email}</strong>.
              We will send a 6-digit code to it.
            </Typography>
            <Button sx={buttonSx} fullWidth disabled={isBusy} onClick={sendConfirmCode}>
              Send code
            </Button>
            <Typography sx={linkSx}>
              <Link to="#" onClick={(event) => { event.preventDefault(); setStep("change"); }}>
                My email is wrong — change it
              </Link>
            </Typography>
          </>
        )}

        {step === "confirmCode" && (
          <EmailCodeBox
            email={profile.email}
            waitSeconds={waitSeconds}
            onConfirm={async (code) => finished((await verifyEmailAPI({ code })).data)}
            onResend={sendEmailCodeAPI}
          >
            <Typography sx={linkSx}>
              <Link to="#" onClick={(event) => { event.preventDefault(); setStep("change"); }}>
                My email is wrong — change it
              </Link>
            </Typography>
          </EmailCodeBox>
        )}

        {step === "change" && (
          <form onSubmit={startChange}>
            <Typography sx={textSx}>
              {isAdding && profile.releasedEmail ? (
                <>
                  <strong>{profile.releasedEmail}</strong> now belongs to another
                  account that confirmed it. Please add an email that is yours.
                  Your bids and history stay with this account.
                </>
              ) : isAdding ? (
                "Please add an email that is yours."
              ) : (
                <>
                  Your email now is <strong>{profile.email}</strong>. Type the new
                  one; it is used only after you type the code we send to it.
                  Your bids and history stay the same.
                </>
              )}
            </Typography>
            <Form.Group className="mb-3" controlId="formNewEmail">
              <Form.Label style={{ color: "#7E8184" }}>New email</Form.Label>
              <Form.Control
                type="email"
                required
                value={newEmail}
                placeholder="Enter your email"
                onChange={(event) => setNewEmail(event.target.value)}
              />
            </Form.Group>
            {profile.hasPassword && (
              <Form.Group className="mb-3" controlId="formCurrentPassword">
                <Form.Label style={{ color: "#7E8184" }}>Your password</Form.Label>
                <Form.Control
                  type="password"
                  required
                  value={password}
                  placeholder="To show it is your account"
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Form.Group>
            )}
            {needsCurrentCode && (
              <Form.Group className="mb-3 otp" controlId="formCurrentCode">
                <Form.Label style={{ color: "#7E8184" }}>
                  Code sent to {profile.email}{" "}
                  <Link to="#" onClick={(event) => { event.preventDefault(); sendCurrentCode(); }}>
                    {currentCodeSent ? "(send again)" : "(send code)"}
                  </Link>
                </Form.Label>
                <OtpInput
                  value={currentCode}
                  onChange={setCurrentCode}
                  numInputs={6}
                  inputType="tel"
                  renderSeparator={<span>-</span>}
                  renderInput={(props) => <input {...props} />}
                />
              </Form.Group>
            )}
            <Button sx={buttonSx} fullWidth type="submit" disabled={isBusy}>
              Send code to the new email
            </Button>
            {!isAdding && !profile.emailVerified && (
              <Typography sx={linkSx}>
                <Link to="#" onClick={(event) => { event.preventDefault(); setStep("confirm"); }}>
                  Back: my email is right
                </Link>
              </Typography>
            )}
            {searchParams.get("mode") === "change" && profile.emailVerified && (
              <Typography sx={linkSx}>
                <Link to={routes.profile}>Cancel</Link>
              </Typography>
            )}
          </form>
        )}

        {step === "changeCode" && (
          <EmailCodeBox
            email={newEmail}
            waitSeconds={waitSeconds}
            onConfirm={async (code) => finished((await verifyChangeEmailAPI({ code })).data)}
            onResend={resendChangeEmailCodeAPI}
          >
            <Typography sx={linkSx}>
              <Link to="#" onClick={(event) => { event.preventDefault(); setStep("change"); }}>
                Use a different email
              </Link>
            </Typography>
          </EmailCodeBox>
        )}

        {!profile.emailVerified && (
          <Typography sx={{ ...linkSx, textAlign: "right" }}>
            <Link to="#" onClick={signOut}>Sign out</Link>
          </Typography>
        )}
      </Card>
    </div>
  );
}
